import React, { useEffect, useRef, useState } from 'react';
import { getMyReferral, trackInvite } from '../api/referral';
import { ReferralData } from '../types/api';

/**
 * Refer-a-friend card.
 *
 * Shows the user's personal referral link with Copy / Share actions,
 * the reward proposition, stats (invited / signed up / subscribed) and
 * available free months from the credits object.
 *
 * Designed to sit inside a `.cvr-card` on the Settings page.
 */
const ReferAFriend: React.FC = () => {
  const [data, setData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    getMyReferral()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load referral info. Please try again later.');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const flashCopied = () => {
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2200);
  };

  const handleCopy = () => {
    if (!data) return;
    navigator.clipboard.writeText(data.link).catch(() => {
      // Fallback for browsers without clipboard API
      const el = document.createElement('textarea');
      el.value = data.link;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    flashCopied();
    // Best-effort tracking — don't await, don't block
    trackInvite('copy').catch(() => {});
  };

  const handleShare = () => {
    if (!data) return;
    if (navigator.share) {
      navigator
        .share({
          title: 'Join me on Cleveroo',
          text: 'Try Cleveroo — the smart way to prepare for OC & selective school exams. Use my link and we both get a free month!',
          url: data.link,
        })
        .catch(() => {
          // User cancelled the share sheet — not an error.
        });
      trackInvite('share').catch(() => {});
    } else {
      // Fallback: copy to clipboard
      handleCopy();
    }
  };

  if (loading) {
    return (
      <div className="cvr-card">
        <h2 className="cvr-card__h">Invite friends</h2>
        <p style={{ fontSize: 14, color: 'var(--mute)' }}>Loading your referral link...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="cvr-card">
        <h2 className="cvr-card__h">Invite friends</h2>
        <p style={{ fontSize: 14, color: 'var(--mute)' }}>
          Invite links aren’t available right now — check back soon.
        </p>
      </div>
    );
  }

  const { link, stats, credits } = data;
  const hasShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <div className="cvr-card">
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <h2 className="cvr-card__h" style={{ margin: 0 }}>Invite friends</h2>
        {credits.availableMonths > 0 && (
          <span
            style={{
              background: 'var(--yellow)',
              color: 'var(--anchor)',
              fontWeight: 800,
              fontSize: 12,
              borderRadius: 999,
              padding: '3px 10px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {credits.availableMonths} free month{credits.availableMonths !== 1 ? 's' : ''} available
          </span>
        )}
      </div>

      {/* Proposition */}
      <p style={{ fontSize: 14, color: 'var(--mute)', margin: '8px 0 18px', lineHeight: 1.5 }}>
        Give a friend a free month — and earn one yourself when they subscribe.
        Share your personal link below.
      </p>

      {/* Link field + action buttons */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
        <input
          readOnly
          value={link}
          className="cvr-input"
          style={{ flex: '1 1 200px', minWidth: 0, cursor: 'text', background: '#f8f5ec' }}
          aria-label="Your referral link"
          onFocus={(e) => e.target.select()}
        />
        <button
          type="button"
          className="cvr-btn"
          onClick={handleCopy}
          aria-label="Copy referral link"
          style={{ flexShrink: 0 }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
        {hasShare && (
          <button
            type="button"
            className="cvr-btn cvr-btn--ghost"
            onClick={handleShare}
            aria-label="Share referral link"
            style={{ flexShrink: 0 }}
          >
            Share
          </button>
        )}
      </div>

      {/* Stats row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 10,
          borderTop: '1px solid var(--line)',
          paddingTop: 16,
        }}
      >
        <StatBox label="Invited" value={stats.invited} />
        <StatBox label="Signed up" value={stats.signedUp} />
        <StatBox label="Subscribed" value={stats.qualified} highlight />
      </div>

      {credits.availableMonths === 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--mute)', marginTop: 14 }}>
          You have no free months yet. Start inviting friends!
        </p>
      )}
    </div>
  );
};

interface StatBoxProps {
  label: string;
  value: number;
  highlight?: boolean;
}

const StatBox: React.FC<StatBoxProps> = ({ label, value, highlight }) => (
  <div
    style={{
      textAlign: 'center',
      background: highlight ? 'var(--green-soft)' : '#f8f5ec',
      borderRadius: 12,
      padding: '12px 8px',
    }}
  >
    <span
      style={{
        display: 'block',
        fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
        fontWeight: 800,
        fontSize: 24,
        color: highlight ? 'var(--anchor)' : 'var(--ink)',
        lineHeight: 1,
      }}
    >
      {value}
    </span>
    <span style={{ display: 'block', fontSize: 12, color: 'var(--mute)', marginTop: 4 }}>
      {label}
    </span>
  </div>
);

export default ReferAFriend;
